import { useCallback, useRef, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from './useDebounce';
import type { Json } from '@/integrations/supabase/types';

interface UserPreferences {
  sidebar_collapsed?: boolean;
  theme?: 'light' | 'dark';
  financial_hidden?: boolean;
  sales_view_mode?: 'table' | 'cards' | 'compact';
  crm_channel_filter?: string[];
  crm_sidebar_open?: boolean;
}

const STORAGE_KEY = 'user_preferences_cache';

export const useUserPreferences = () => {
  const [preferences, setPreferences] = useState<UserPreferences>({});
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const isInitialized = useRef(false);

  // Carregar preferências ao montar
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          setUserId(user.id);
          
          // Buscar do banco
          const { data, error } = await supabase
            .from('tenant_users')
            .select('dashboard_preferences')
            .eq('id', user.id)
            .single();

          if (!error && data?.dashboard_preferences) {
            const prefs = data.dashboard_preferences as UserPreferences;
            setPreferences(prefs);
            // Cache local
            localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
          } else {
            // Tentar localStorage como fallback
            const cached = localStorage.getItem(STORAGE_KEY);
            if (cached) {
              setPreferences(JSON.parse(cached));
            }
          }
        } else {
          // Sem usuário, usar localStorage
          const cached = localStorage.getItem(STORAGE_KEY);
          if (cached) {
            setPreferences(JSON.parse(cached));
          }
        }
      } catch (error) {
        console.warn('Erro ao carregar preferências:', error);
        // Fallback para localStorage
        const cached = localStorage.getItem(STORAGE_KEY);
        if (cached) {
          setPreferences(JSON.parse(cached));
        }
      } finally {
        setIsLoading(false);
        isInitialized.current = true;
      }
    };

    loadPreferences();
  }, []);

  // Função para salvar no banco (será usada com debounce)
  const saveToDatabase = useCallback(async (newPrefs: UserPreferences) => {
    if (!userId) return;

    try {
      await supabase
        .from('tenant_users')
        .update({ dashboard_preferences: newPrefs as Json })
        .eq('id', userId);
    } catch (error) {
      console.warn('Erro ao salvar preferências:', error);
    }
  }, [userId]);

  // Função para salvar IMEDIATAMENTE (sem debounce) - para preferências críticas como tema
  const saveImmediately = useCallback(async (newPrefs: UserPreferences) => {
    if (!userId) return;

    try {
      console.log('💾 Salvando tema no banco:', newPrefs);
      const { error } = await supabase
        .from('tenant_users')
        .update({ dashboard_preferences: newPrefs as Json })
        .eq('id', userId);
      
      if (error) {
        console.error('❌ Erro ao salvar tema:', error);
      } else {
        console.log('✅ Tema salvo com sucesso no banco!');
      }
    } catch (error) {
      console.error('Erro ao salvar preferências:', error);
    }
  }, [userId]);

  // Debounce para salvar no banco
  const debouncedSave = useDebounce(saveToDatabase, 500);

  // Atualizar uma preferência específica
  const updatePreference = useCallback(<K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ) => {
    setPreferences(prev => {
      const newPrefs = { ...prev, [key]: value };
      
      // Cache local imediatamente
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newPrefs));
      
      // Salvar no banco com debounce
      if (userId) {
        debouncedSave(newPrefs);
      }
      
      return newPrefs;
    });
  }, [userId, debouncedSave]);

  // Helpers específicos para sidebar
  const getSidebarCollapsed = useCallback(() => {
    return preferences.sidebar_collapsed ?? false;
  }, [preferences.sidebar_collapsed]);

  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    updatePreference('sidebar_collapsed', collapsed);
  }, [updatePreference]);

  // Helpers específicos para tema
  const getTheme = useCallback(() => {
    return preferences.theme ?? 'light';
  }, [preferences.theme]);

  // Salvar tema IMEDIATAMENTE (sem debounce) para garantir persistência
  const setThemePreference = useCallback((theme: 'light' | 'dark') => {
    setPreferences(prev => {
      const newPrefs = { ...prev, theme };
      
      // Cache local imediatamente
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newPrefs));
      
      // Salvar IMEDIATAMENTE no banco (sem debounce)
      if (userId) {
        saveImmediately(newPrefs);
      }
      
      return newPrefs;
    });
  }, [userId, saveImmediately]);

  const getFinancialHidden = useCallback(() => {
    return preferences.financial_hidden ?? false;
  }, [preferences.financial_hidden]);

  const setFinancialHidden = useCallback((hidden: boolean) => {
    updatePreference('financial_hidden', hidden);
  }, [updatePreference]);

  const getSalesViewMode = useCallback(() => {
    return preferences.sales_view_mode ?? 'table';
  }, [preferences.sales_view_mode]);

  const setSalesViewMode = useCallback((mode: 'table' | 'cards' | 'compact') => {
    updatePreference('sales_view_mode', mode);
  }, [updatePreference]);

  const getCrmChannelFilter = useCallback(() => {
    return preferences.crm_channel_filter ?? [];
  }, [preferences.crm_channel_filter]);

  const setCrmChannelFilter = useCallback((ids: string[]) => {
    updatePreference('crm_channel_filter', ids);
  }, [updatePreference]);

  const getCrmSidebarOpen = useCallback(() => {
    return preferences.crm_sidebar_open ?? false;
  }, [preferences.crm_sidebar_open]);

  const setCrmSidebarOpen = useCallback((open: boolean) => {
    updatePreference('crm_sidebar_open', open);
  }, [updatePreference]);

  return {
    preferences,
    isLoading,
    updatePreference,
    getSidebarCollapsed,
    setSidebarCollapsed,
    getTheme,
    setThemePreference,
    getFinancialHidden,
    setFinancialHidden,
    getSalesViewMode,
    setSalesViewMode,
    getCrmChannelFilter,
    setCrmChannelFilter,
    getCrmSidebarOpen,
    setCrmSidebarOpen,
  };
};
